import { ReactNode, useEffect } from 'react'
import { useNavigate } from 'react-router'

import { Source } from '@ui/__generated__/graphql'
import { DatableColoredIcon } from '@ui/components/icons'
import { useHasStack } from '@ui/lib/hooks/useHasStack'
import { AssignSourceToPipelineQuery, GetOnboardablePipelineQuery } from '@ui/loaders/onboarding'
import { FeatureFlagName, useFeatureFlag } from '@ui/providers/FeatureFlagProvider'
import clsx from 'clsx'

import { OnboardingStep, SelectableSourceType, useOnboarding } from './components/hooks/useOnboarding'
import {
  ConfigurationSuccessful,
  ConfigurationUnsuccessful,
  SettingUpSource,
  SourceConfiguration,
  SourceTypeSelection,
  StartTransforming,
} from './components/OnboardingSteps'

enum TabName {
  SelectSource = 'Select Source',
  ConfigureSource = 'Configure Your Source',
}

type TabProps<T extends string> = {
  activeTab: T
  tabs: T[]
}

const Tabs = <T extends string>({ activeTab, tabs }: TabProps<T>) => {
  return (
    <header className="sticky top-0 flex h-14 bg-white" aria-label="Tabs">
      <div className="absolute left-4 flex h-14 items-center">
        <DatableColoredIcon />
      </div>
      {tabs.map((tab) => {
        const isActive = activeTab === tab

        return (
          <div
            key={tab}
            className={clsx(
              {
                'border-primary-400 text-gray-900': isActive,
                'border-gray-200': !isActive,
              },
              'flex-1 border-b-4 px-1 py-4 text-center text-sm font-medium text-gray-400'
            )}>
            {tab}
          </div>
        )
      })}
    </header>
  )
}

export const OnboardSource = () => {
  const { data: onboardablePipelineData } = GetOnboardablePipelineQuery()
  const [assignSourceToPipeline] = AssignSourceToPipelineQuery()

  const assignSourceToPipelineEnabled = useFeatureFlag(FeatureFlagName.AssignSourceToPipeline)

  const { step, setStep, setSelectedSourceType, selectedSourceType, onDataSourceConfigured, detectSource } =
    useOnboarding()

  const navigate = useNavigate()

  const { loading, hasStack } = useHasStack()

  const onSourceDetected = async (source: Source) => {
    try {
      if (assignSourceToPipelineEnabled && onboardablePipelineData?.onboardablePipeline) {
        await assignSourceToPipeline({
          variables: {
            input: {
              sourceId: source.id,
              pipelineId: onboardablePipelineData.onboardablePipeline.id,
            },
          },
        })
      }
    } catch (err) {
      console.error(err)
    } finally {
      setStep(OnboardingStep.ConfigurationSuccessful)
    }
  }

  let content: ReactNode
  switch (step) {
    case OnboardingStep.SelectSourceType:
      content = (
        <SourceTypeSelection
          selectedSourceType={selectedSourceType as SelectableSourceType}
          onSelectSourceType={setSelectedSourceType}
          onNext={() => setStep(OnboardingStep.ConfigureSource)}
          onBack={() => navigate('/setup')}
        />
      )
      break
    case OnboardingStep.ConfigureSource:
      content = (
        <SourceConfiguration
          selectedSourceType={selectedSourceType as SelectableSourceType}
          onNext={onDataSourceConfigured}
          onBack={() => setStep(OnboardingStep.SelectSourceType)}
        />
      )
      break
    case OnboardingStep.DetectingSource:
      content = (
        <SettingUpSource
          onBack={() => setStep(OnboardingStep.ConfigureSource)}
          detectSource={detectSource}
          onFailure={() => setStep(OnboardingStep.ConfigurationUnsuccessful)}
          onSuccess={onSourceDetected}
        />
      )
      break
    case OnboardingStep.ConfigurationSuccessful:
      content = (
        <ConfigurationSuccessful
          onboardablePipelineId={
            assignSourceToPipelineEnabled ? onboardablePipelineData?.onboardablePipeline?.id : undefined
          }
        />
      )
      break
    case OnboardingStep.ConfigurationUnsuccessful:
      content = <ConfigurationUnsuccessful onBack={() => setStep(OnboardingStep.ConfigureSource)} />
      break
    case OnboardingStep.StartTransforming:
      content = <StartTransforming onBack={() => setStep(OnboardingStep.SelectSourceType)} />
      break
  }

  const activeTab = step === OnboardingStep.SelectSourceType ? TabName.SelectSource : TabName.ConfigureSource

  useEffect(() => {
    if (!loading && !hasStack) {
      navigate('/setup')
    }
  }, [loading, hasStack])

  return (
    <main className="flex h-screen w-screen flex-col bg-white">
      <Tabs activeTab={activeTab} tabs={Object.values(TabName)} />
      {content}
    </main>
  )
}
